<template>
  <div class="block-wrapper">
    <label class="label" :for="name" v-if="label">{{ label }}</label>
    <SearchInput
      v-if="isSearchable"
      class="search-input"
      v-model="searchTerm"
      ref="searchInput"
      placeholder='Search'
      :name="name"
    >
      <ClearIcon
        v-if="searchTerm"
        class="clear"
        @click.native="searchTerm = ''"
      ></ClearIcon>
    </SearchInput>
    <div class="options" v-if="filteredOptions.length">
      <div class="select-links">
        <a class="select-all-link" @click="markSelections(true)">Select All</a>
        <a class="deselect-all-link" @click="markSelections(false)">Deselect All</a>
      </div>
      <div v-for="filter in filteredOptions" :key="filter.key" class="option" >
        <Checkbox
          v-model="filter.checked"
          :name="filter.value"
          @change="handleSelect"
        >
          {{ filter.key }}
        </Checkbox>
      </div>
    </div>
    <div v-else class="options no-results">
      <slot name="noResults">
        <LoadingDarkIcon v-if="isFetching" />
        {{ loadingMessage }}
      </slot>
    </div>
  </div>
</template>
<script>
import { mapState } from 'vuex';
import {
  getReportFieldFromValue,
} from '../../../services/dashboard/reportFieldService';
import { getCurrencyFromString } from '../../../services/dashboard/dashboardUtils';
import {
  SearchInput,
  Checkbox,
  ClearIcon,
  LoadingDarkIcon,
} from '@mikmak/components';
export default {
  props: {
    label: {
      type: String,
    },
    name: {
      type: String,
    },
    value: {
      type: Array,
      required: true,
      validator: value => {
        return value.every(v => {
          return v.key && v.value !== undefined;
        });
      },
    },
    dimensionName: {
      type: String,
      default: '',
    },
    isSearchable: {
      type: Boolean,
      default: true,
    },
    isFetching: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      searchTerm: '',
      localVal: this.value,
    };
  },
  components: {
    SearchInput,
    Checkbox,
    ClearIcon,
    LoadingDarkIcon,
  },
  computed: {
    ...mapState({
      allReportFields: state => state.dashboard.availableReportFields.allReportFields,
    }),
    lowerCaseSearchTerm() {
      return this.searchTerm.toLowerCase();
    },
    sortedOptions() {
      const dimension = getReportFieldFromValue(this.allReportFields, this.dimensionName);

      if (dimension) {
        return [...this.localVal].sort((a, b) => {
          a = a.value;
          b = b.value;

          if (dimension.sortType === 'currency') {
            a = getCurrencyFromString(a);
            b = getCurrencyFromString(b);
          }

          if (dimension.sortType === 'custom') {
            const customOrder = dimension.itemOrder;

            a = customOrder.indexOf(a.toLowerCase());
            b = customOrder.indexOf(b.toLowerCase());
          }

          if (a === b) return 0;
          else if (a === null) return 1;
          else if (b === null) return -1;
          else
            return (a > b ? 1 : -1);
        });
      }

      return [];
    },
    filteredOptions() {
      if (!this.lowerCaseSearchTerm) {
        return this.sortedOptions;
      }

      return this.sortedOptions.filter(v => {
        const key = v.key.toLowerCase();

        return key.includes(this.lowerCaseSearchTerm);
      });
    },
    hasSelected() {
      return this.localVal.some(filter => filter.checked);
    },
    loadingMessage() {
      return this.isFetching ? 'Loading Values' : 'No Results Found';
    },
  },
  methods: {
    handleCancel() {
      this.searchTerm = '';
      this.$emit('cancel');
    },
    markSelections(isChecked) {
      const updatedLocalVal = this.localVal.map(v => {
        const key = v.key.toLowerCase();

        return {
          ...v,
          checked: key.includes(this.lowerCaseSearchTerm) ? isChecked : v.checked,
        };
      });

      updatedLocalVal.sort((a, b) => a.key.localeCompare(b.key));
      this.localVal = updatedLocalVal;
      this.handleSelect();
    },
    handleSelect() {
      this.$emit('submit', this.localVal);
    },
  },
  watch: {
    value() {
      this.localVal = this.value;
    },
  },
};
</script>
<style scoped>
::-webkit-scrollbar {
  -webkit-appearance: none;
  width: 6px;
}
::-webkit-scrollbar-thumb {
  border-radius: 4px;
  background-color: rgba(0, 0, 0, .5);
  box-shadow: 0 0 1px rgba(255, 255, 255, .5);
}
.select-links {
  padding-bottom: 5px;
}
.option {
  padding: 5px 0px;
}
.block-wrapper {
  display: flex;
  flex-direction: column;
  width: 100%;
  height: 100%;
  font-family: Lato;
  font-style: normal;
  font-weight: normal;
  font-size: 16px;
  line-height: 100%;
  color: #223448;
  border: 1.5px solid #778699;
  border-radius: 4px;
}
.block-wrapper .label {
  line-height: 19px;
  margin-bottom: 16px;
  color: #223448;
}
.clear {
  right: 12px;
  cursor: pointer;
}
.search-input {
  margin: 0;
}
.search-input >>> input[type="text"] {
  border: none;
  border-radius: 0;
  border-bottom: 1.5px solid #c1ccd4;
}
.options {
  height: 100%;
  min-height: 150px;
  max-height: 450px;
  background: #ffffff;
  box-sizing: border-box;
  padding: 14px 15px 4px 15px;
  overflow: auto;
  color: #2c3f58;
  overflow-y: scroll;
}
.no-results {
  display: flex;
  justify-content: center;
  align-items: center;
  color: #778699;
  height: 158px;
}
.select-all {
  font-weight: bold;
}
.select-all-link {
  color: #4369F4;
  font-weight: 700;
  text-align: left;
}
.deselect-all-link {
  float: right;
  color: #4369F4;
  font-weight: 700;
}
</style>
